import { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ModalProps,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { CreateSandboxUserDTO } from '../../../../../api';

export type ActionModalProps = {
  open: boolean;
  onClose: () => void;
  fieldsHandler: (
    e: SelectChangeEvent | ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  fields: CreateSandboxUserDTO['body'] | null;
  onCreate: () => void;
  isLoading: boolean;
};

export const CreateModal: FC<ActionModalProps> = (props) => {
  const { open, onClose, fields, fieldsHandler, onCreate, isLoading } = props;
  const { t } = useTranslation();

  // validation for required fields
  const requiredFields = ['email'] as (keyof CreateSandboxUserDTO['body'])[];
  const checkSubmitDisabling = () => {
    if (!fields) return true;

    for (const field of requiredFields) {
      if (fields[field] === undefined) {
        return true;
      }
    }
    return false;
  };

  // close handlers
  const closeHandlerWithPrevent: ModalProps['onClose'] = (_, reason) => {
    if (reason === 'backdropClick') return;

    onClose();
  };
  const closeHandler = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => closeHandlerWithPrevent(_, reason)}
      PaperProps={{ sx: { minWidth: '380px' } }}
    >
      <DialogTitle>{t('sandbox-users.create-modal.title')}</DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={() => closeHandler()}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Stack direction="column" spacing={2}>
          <TextField
            name="email"
            value={fields?.email ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('sandbox-users.create-modal.fields.email')}
            fullWidth
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={() => onCreate()}
          disabled={isLoading || checkSubmitDisabling()}
        >
          {t('fees.modals.create.create-btn')}
        </Button>
        <Button variant="outlined" onClick={() => onClose()}>
          {t('fees.modals.cancel-btn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
